import React, { useState, useRef, useEffect } from 'react';
import { averageSegments } from '../utils/doctorDashboardUtils';
import { analyzeAudioLevel } from '../utils/audioLevelAnalysis';
import { authFetch, authHeaders } from '../utils/authFetch';
import {
  doctorStartSession,
  doctorSaveEmotions,
  doctorAddNote,
} from './api';
import { EMOTION_KEYS } from '../utils/emotionConstants';

function mapApiResponseToDoctor(apiResult) {
  const res = (apiResult && (apiResult.RES || apiResult.res || apiResult.data || apiResult)) || {};
  const top5String = res.top_5 || '';
  const scores = {};

  EMOTION_KEYS.forEach(em => {
    try {
      const re = new RegExp(`${em}\\s+(\\d+(?:\\.\\d+)?)%`, 'i');
      const m = top5String.match(re);
      const value = m ? parseFloat(m[1]) / 100 : 0;
      scores[em] = value;
    } catch (error) {
      scores[em] = 0;
    }
  });

  let correctedScores = { ...scores };

  const totalNegative = (scores.sad || 0) + (scores.fear || 0) + (scores.disgust || 0);
  const isLikelyMisclassified = (
    scores.angry > 0.5 &&
    scores.happy > 0.1 &&
    totalNegative < 0.2 &&
    !scores.sad
  );

  if (isLikelyMisclassified) {
    const totalEmotion = scores.angry + scores.happy;
    correctedScores.happy = Math.min(0.8, totalEmotion * 0.7);
    correctedScores.angry = Math.max(0.1, totalEmotion * 0.3);
  }

  const joy = Math.max(0, Math.min(1, correctedScores.happy ?? 0));
  const sadness = Math.max(0, Math.min(1, correctedScores.sad ?? 0));
  const anger = Math.max(0, Math.min(1, correctedScores.angry ?? 0));
  const stress = Math.max(0, Math.min(1, correctedScores.fear ?? 0));
  const hurt = Math.max(0, Math.min(1, correctedScores.disgust ?? 0));

  const burnout = Math.round(((correctedScores.angry || 0) + (correctedScores.sad || 0) + (correctedScores.fear || 0) + (correctedScores.disgust || 0)) * 100) || 0;

  const result = { joy, stress, sadness, anger, hurt, burnout_score: burnout };

  return result;
}

async function callPredictAPI(audioBlob, retryCount = 0) {
  const PREDICT_ENDPOINT = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_PREDICT_URL)
    ? process.env.REACT_APP_PREDICT_URL
    : ((typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'))
      ? 'http://localhost:5000/predict'
      : 'https://app.neurolonic.com/app/predict');

  try {
    if (audioBlob.size < 100) {
      return {
        joy: 0,
        stress: 0,
        sadness: 0,
        anger: 0,
        hurt: 0,
        burnout_score: 0,
        _dataSource: 'silence_detected',
        _message: 'Audio file too small - likely silent recording',
        _fileSize: audioBlob.size
      };
    }

    let audioAnalysis;
    try {
      audioAnalysis = await Promise.race([
        analyzeAudioLevel(audioBlob),
        new Promise(resolve => setTimeout(() => resolve({ isSilent: false, reason: 'timeout' }), 5000))
      ]);
    } catch (error) {
      console.error('Audio analysis failed:', error);
      audioAnalysis = { isSilent: false, reason: 'analysis_failed', error: error.message };
    }

    if (audioAnalysis.isSilent) {
      return {
        joy: 0,
        stress: 0,
        sadness: 0,
        anger: 0,
        hurt: 0,
        burnout_score: 0,
        _dataSource: 'silence_detected',
        _silenceAnalysis: audioAnalysis,
        _message: 'No speech detected - likely background noise or silence'
      };
    }

    const formData = new FormData();
    formData.append('file', audioBlob, 'audio.wav');
    const startTs = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);

    const response = await fetch(PREDICT_ENDPOINT, {
      method: 'POST',
      body: formData,
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`API call failed: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();
    const endTs = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();

    const mapped = mapApiResponseToDoctor(result);

    if (mapped && typeof mapped === 'object') {
      const totalEmotion = (mapped.joy || 0) + (mapped.stress || 0) + (mapped.sadness || 0) + (mapped.anger || 0) + (mapped.hurt || 0);
      const isLikelyNoise = (
        totalEmotion < 0.1 ||
        (mapped.anger < 0.05 && mapped.joy < 0.05 && mapped.sadness < 0.05 && mapped.stress < 0.05 && mapped.hurt < 0.05)
      );

      if (isLikelyNoise) {
        return {
          joy: 0,
          stress: 0,
          sadness: 0,
          anger: 0,
          hurt: 0,
          burnout_score: 0,
          _dataSource: 'noise_detected_post_api',
          _apiResponse: result,
          _message: 'Background noise detected by API analysis'
        };
      }

      mapped._dataSource = 'predict_api';
      mapped._apiResponse = result;
      mapped._endpoint = PREDICT_ENDPOINT;
      mapped._latency = Math.round(endTs - startTs);
      mapped._audioAnalysis = audioAnalysis;
      return mapped;
    }

    console.warn('⚠️ INVALID API RESPONSE - Using fallback data');
    return {
      joy: 0, stress: 0, sadness: 0, anger: 0, hurt: 0, burnout_score: 0,
      _dataSource: 'fallback_empty',
      _reason: 'invalid_api_response'
    };
  } catch (error) {
    console.error('❌ PREDICT API ERROR:', error);

    if (retryCount < 2 && (
      error.name === 'AbortError' ||
      error.message.includes('ERR_CONNECTION_RESET') ||
      error.message.includes('Failed to fetch') ||
      error.message.includes('Network Error')
    )) {
      await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
      return callPredictAPI(audioBlob, retryCount + 1);
    }

    return {
      joy: 0, stress: 0, sadness: 0, anger: 0, hurt: 0, burnout_score: 0,
      _dataSource: 'error_empty',
      _error: error.message,
      _retryCount: retryCount
    };
  }
}

export default function SessionModal({ patient, onClose, onSessionSaved }) {
  const [timer, setTimer] = useState(0);
  const [running, setRunning] = useState(false);
  const [micClicks, setMicClicks] = useState(0);
  const [segmentRecording, setSegmentRecording] = useState(false);
  const [segmentResults, setSegmentResults] = useState([]);
  const [sessionComplete, setSessionComplete] = useState(false);
  const [finalResults, setFinalResults] = useState(null);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);

  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [, setSessionId] = useState(null);
  const [noteRecState, setNoteRecState] = useState('idle');
  const [noteError, setNoteError] = useState('');

  const [doctorAssessment, setDoctorAssessment] = useState(null);

  const noteMediaRecorderRef = useRef(null);
  const noteAudioChunksRef = useRef([]);
  const noteStreamRef = useRef(null);

  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => setTimer(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [running]);

  useEffect(() => {
    return () => {
      try {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
          mediaRecorderRef.current.stop();
        }
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
        }
        if (noteMediaRecorderRef.current && noteMediaRecorderRef.current.state === 'recording') {
          noteMediaRecorderRef.current.stop();
        }
        if (noteStreamRef.current) {
          noteStreamRef.current.getTracks().forEach(track => track.stop());
        }
      } catch (_) { }
    };
  }, []);

  function hhmmss(total) {
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    return [h, m, s].map(v => String(v).padStart(2, '0')).join(':');
  }

  async function startRecording() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });

        if (micClicks > 1) {
          await new Promise(resolve => setTimeout(resolve, 2000));
        }

        const result = await callPredictAPI(audioBlob);

        setSegmentResults(prev => {
          const next = [...prev, result];
          if (next.length === 5) {
            const silentCount = next.filter(r =>
              r._dataSource === 'silence_detected' ||
              r._dataSource === 'noise_detected_post_api'
            ).length;
            const validResults = next.filter(r =>
              r._dataSource !== 'silence_detected' &&
              r._dataSource !== 'noise_detected_post_api'
            );

            if (silentCount === 5) {
              setFinalResults({
                emotions: { joy: 0, stress: 0, sadness: 0, anger: 0, hurt: 0 },
                burnout_score: 0,
                _dataSource: 'all_silent',
                _message: 'No meaningful audio input detected in any recording'
              });
            } else if (silentCount >= 3) {
              const avg = validResults.length > 0 ? averageSegments(validResults) : null;
              if (avg) {
                avg._dataSource = 'partial_silence';
                avg._silentCount = silentCount;
                avg._validCount = validResults.length;
                avg._message = `Only ${validResults.length} out of 5 recordings contained meaningful audio`;
                setFinalResults(avg);
              }
            } else {
              const avg = averageSegments(validResults.length > 0 ? validResults : next);
              if (avg) {
                avg._silentCount = silentCount;
                avg._validCount = validResults.length;
                setFinalResults(avg);
              }
            }

            setSessionComplete(true);
            setRunning(false);
          }
          return next;
        });
      };

      mediaRecorder.start();
      setSegmentRecording(true);

      setTimeout(() => {
        if (mediaRecorder.state === 'recording') {
          mediaRecorder.stop();
          setSegmentRecording(false);
        }
      }, 8000);
    } catch (error) {
      console.error('Recording failed:', error);
      alert('Microphone access denied or recording failed. Please check permissions.');
      setSegmentRecording(false);
    }
  }

  function handleMicClick() {
    if (!patient) return;
    if (micClicks >= 5 || segmentRecording) return;

    if (micClicks === 0) {
      setTimer(0);
      setRunning(true);
      setSegmentResults([]);
      setSessionComplete(false);
      setFinalResults(null);
    }

    setMicClicks(c => c + 1);
    startRecording();
  }

  const saveSession = async () => {
    if (!patient || !finalResults) {
      alert('Please complete all 5 recordings before saving');
      return;
    }

    if (!doctorAssessment) {
      alert('Please select the doctor\'s clinical assessment before saving the session.');
      return;
    }

    if (finalResults._dataSource === 'all_silent') {
      alert('Cannot save session: No meaningful speech was detected in any recording. This could be due to background noise (fans, vehicles, etc.) or silence. Please record again with clear speech in a quieter environment.');
      return;
    }

    setBusy(true);
    try {
      const startResp = await doctorStartSession(patient.id, Math.max(1, timer));
      const sid = startResp?.data?.session_id || startResp?.data?.sessionId || startResp?.session_id;
      setSessionId(sid);

      const emotionData = {
        doctor_patient_id: patient.id,
        session_id: sid,
        joy: Math.round(finalResults.emotions.joy * 100),
        stress: Math.round(finalResults.emotions.stress * 100),
        sadness: Math.round(finalResults.emotions.sadness * 100),
        anger: Math.round(finalResults.emotions.anger * 100),
        burnout_score: finalResults.burnout_score,
        doctor_assessment: doctorAssessment,
      };

      await doctorSaveEmotions(emotionData);

      if (note && note.trim().length > 0) {
        await doctorAddNote(sid, note.trim());
      }

      alert('Session saved successfully');
      if (onSessionSaved) {
        onSessionSaved();
      }
      onClose();
    } catch (e) {
      console.error('❌ SAVE FAILED:', e);
      alert('Failed to save session');
    } finally {
      setBusy(false);
    }
  };

  const startNoteRecording = async () => {
    if (noteRecState === 'recording' || noteRecState === 'transcribing') return;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      noteStreamRef.current = stream;
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : undefined;
      const mr = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      noteMediaRecorderRef.current = mr;
      noteAudioChunksRef.current = [];

      mr.ondataavailable = (ev) => {
        if (ev.data && ev.data.size > 0) noteAudioChunksRef.current.push(ev.data);
      };

      mr.onstop = async () => {
        try {
          const blob = new Blob(noteAudioChunksRef.current, { type: mimeType || 'audio/webm' });
          if (noteStreamRef.current) noteStreamRef.current.getTracks().forEach(t => t.stop());
          await transcribeNoteAudio(blob);
        } finally {
          noteAudioChunksRef.current = [];
        }
      };

      mr.start();
      setNoteRecState('recording');

      setTimeout(() => {
        try {
          if (noteMediaRecorderRef.current && noteMediaRecorderRef.current.state === 'recording') {
            noteMediaRecorderRef.current.stop();
          }
        } catch { }
      }, 60000);
    } catch (e) {
      console.error('Note recording failed:', e);
      setNoteError('Microphone access denied or recording failed');
      setNoteRecState('error');
      setTimeout(() => setNoteRecState('idle'), 2500);
    }
  };

  const stopNoteRecording = () => {
    try {
      if (noteMediaRecorderRef.current && noteMediaRecorderRef.current.state === 'recording') {
        noteMediaRecorderRef.current.stop();
      }
    } catch { }
  };

  const transcribeNoteAudio = async (audioBlob) => {
    try {
      setNoteRecState('transcribing');
      setNoteError('');

      const useServer = true;
      let text = '';
      if (useServer) {
        const fd = new FormData();
        fd.append('file', audioBlob, 'note-recording.webm');
        fd.append('language', 'en');
        const base = process.env.REACT_APP_API_URL || '/api';
        const r = await authFetch(`${base}/transcribe`, { method: 'POST', headers: authHeaders(), body: fd });
        if (!r.ok) {
          const t = await r.text().catch(() => '');
          throw new Error(`Server transcription failed (${r.status}): ${t || r.statusText}`);
        }
        const j = await r.json();
        if (j && j.status_code === 200 && j.data && typeof j.data.text === 'string') {
          text = j.data.text;
        } else {
          text = '';
        }
      } else {
        const fromProcess = (typeof process !== 'undefined' && process.env)
          ? (process.env.OPENAI_API_KEY || process.env.REACT_APP_OPENAI_API_KEY)
          : undefined;
        const fromWindow = (typeof window !== 'undefined' && window.OPENAI_API_KEY) ? window.OPENAI_API_KEY : undefined;
        const apiKey = fromProcess || fromWindow;
        if (!apiKey) throw new Error('Missing OPENAI_API_KEY');
        const form = new FormData();
        form.append('file', audioBlob, 'note-recording.webm');
        form.append('model', 'whisper-1');
        const res = await fetch('https://api.openai.com/v1/audio/transcriptions', {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${apiKey}` },
          body: form
        });
        if (!res.ok) {
          const t = await res.text().catch(() => '');
          throw new Error(`Transcription failed (${res.status}): ${t || res.statusText}`);
        }
        const data = await res.json();
        text = (data && (data.text || data.text === '' ? data.text : data.result)) || '';
      }

      if (typeof text === 'string') {
        setNote(prev => (prev ? (prev + (prev.endsWith('\n') ? '' : '\n') + text) : text));
      }
      setNoteRecState('idle');
    } catch (e) {
      console.error('Note transcription error:', e);
      setNoteError(e?.message || 'Transcription error');
      setNoteRecState('error');
      setTimeout(() => setNoteRecState('idle'), 2500);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>New Session - {patient?.name}</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="session-grid">
            <div className="session-left">
              <div className="patient-brief">
                <h3>Patient Information</h3>
                <p><strong>Age:</strong> <span>{patient?.age || 'Not specified'}</span></p>
                <p><strong>Contact:</strong> <span>{patient?.contact || 'Not provided'}</span></p>
                <p><strong>Status:</strong> <span>{patient?.lastMentalHealth || 'No Data'}</span></p>
              </div>

              <div className="session-timer">
                <h3>Session Timer</h3>
                <div className="timer-display">{hhmmss(timer)}</div>
                <div className="timer-actions">
                  <button className="btn-primary" onClick={() => setRunning(true)} disabled={running}>Start</button>
                  <button className="btn-secondary" onClick={() => setRunning(false)} disabled={!running}>Pause</button>
                </div>
              </div>

              <div className="recording-section">
                <h3>Voice Recording</h3>
                <button
                  className="btn-primary mic-btn"
                  onClick={handleMicClick}
                  disabled={micClicks >= 5 || segmentRecording}
                >
                  {micClicks >= 5 ? 'Complete' : (segmentRecording ? 'Recording 8s…' : `🎤 Record (${micClicks}/5)`)}
                </button>
                <div className="recording-info">
                  <p>Click up to 5 times. Each records 8s and analyzes emotions.</p>
                  {segmentResults.length > 0 && (
                    <p>Segments completed: {segmentResults.length}/5</p>
                  )}
                </div>
              </div>

              <div className="notes-box">
                <div className="notes-header">
                  <h3>Session Notes</h3>
                  {noteRecState !== 'recording' ? (
                    <button className="btn-secondary" onClick={startNoteRecording}>🎤 Record Note</button>
                  ) : (
                    <button className="btn-secondary" onClick={stopNoteRecording}>⏹ Stop</button>
                  )}
                </div>
                {noteRecState !== 'idle' && (
                  <div className="note-status">
                    {noteRecState === 'recording' && 'Recording… Click Stop to transcribe.'}
                    {noteRecState === 'transcribing' && 'Transcribing…'}
                    {noteRecState === 'error' && (noteError || 'Error')}
                  </div>
                )}
                <textarea
                  rows="5"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Type or record notes here..."
                />
              </div>
            </div>

            <div className="session-right">
              <h3>Emotion Analysis</h3>
              {!sessionComplete ? (
                <p className="muted">Complete all 5 recordings to see results</p>
              ) : finalResults._dataSource === 'all_silent' ? (
                <div className="silence-warning">
                  <div className="warning-icon">🔇</div>
                  <h4>No Audio Input Detected</h4>
                  <p>All recordings were silent or contained no meaningful audio input. Please ensure you speak clearly during recording.</p>
                  <div className="silence-tips">
                    <p><strong>Tips:</strong></p>
                    <ul>
                      <li>Speak directly into the microphone</li>
                      <li>Ensure microphone permissions are enabled</li>
                      <li>Check your microphone volume settings</li>
                      <li>Try speaking louder or closer to the microphone</li>
                    </ul>
                  </div>
                </div>
              ) : finalResults._dataSource === 'partial_silence' ? (
                <div className="partial-silence-warning">
                  <div className="warning-icon">⚠️</div>
                  <h4>Limited Audio Input</h4>
                  <p>{finalResults._message}</p>
                  <p className="muted">Results based on {finalResults._validCount} valid recording(s):</p>
                  <div className="emotion-cards">
                    <div className="emotion-card">Joy <span>{Math.round(finalResults.emotions.joy * 100)}%</span></div>
                    <div className="emotion-card">Stress <span>{Math.round(finalResults.emotions.stress * 100)}%</span></div>
                    <div className="emotion-card">Sadness <span>{Math.round(finalResults.emotions.sadness * 100)}%</span></div>
                    <div className="emotion-card">Anger <span>{Math.round(finalResults.emotions.anger * 100)}%</span></div>
                    <div className="emotion-card">Hurt <span>{Math.round(finalResults.emotions.hurt * 100)}%</span></div>
                    <div className="emotion-card highlight">Burnout <span>{finalResults.burnout_score}%</span></div>
                  </div>
                </div>
              ) : (
                <div className="emotion-results">
                  {finalResults._silentCount > 0 && (
                    <p className="muted">Note: {finalResults._silentCount} recording(s) contained no audio</p>
                  )}
                  <div className="emotion-cards">
                    <div className="emotion-card">Joy <span>{Math.round(finalResults.emotions.joy * 100)}%</span></div>
                    <div className="emotion-card">Stress <span>{Math.round(finalResults.emotions.stress * 100)}%</span></div>
                    <div className="emotion-card">Sadness <span>{Math.round(finalResults.emotions.sadness * 100)}%</span></div>
                    <div className="emotion-card">Anger <span>{Math.round(finalResults.emotions.anger * 100)}%</span></div>
                    <div className="emotion-card">Hurt <span>{Math.round(finalResults.emotions.hurt * 100)}%</span></div>
                    <div className="emotion-card highlight">Burnout <span>{finalResults.burnout_score}%</span></div>
                  </div>
                </div>
              )}

              {sessionComplete && (
                <div className="clinical-assessment-section">
                  <h3>Clinical Assessment</h3>
                  <p className="muted">Select the doctor's clinical assessment based on the session:</p>
                  <div className="assessment-options">
                    <button
                      className={`assessment-btn ${doctorAssessment === 'Stable' ? 'active' : ''}`}
                      onClick={() => setDoctorAssessment('Stable')}
                    >
                      🟢 Stable
                    </button>
                    <button
                      className={`assessment-btn ${doctorAssessment === 'Moderate Risk' ? 'active' : ''}`}
                      onClick={() => setDoctorAssessment('Moderate Risk')}
                    >
                      🟠 Moderate Risk
                    </button>
                    <button
                      className={`assessment-btn ${doctorAssessment === 'High Risk' ? 'active' : ''}`}
                      onClick={() => setDoctorAssessment('High Risk')}
                    >
                      🔴 High Risk
                    </button>
                  </div>
                </div>
              )}

              <div className="actions">
                <button
                  className="btn-primary"
                  onClick={saveSession}
                  disabled={busy || !sessionComplete}
                >
                  {busy ? 'Saving...' : 'Save Session'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
